// Sites round 2 (D16) — "drop it and it's live". Pure helpers (tested with plain Node) plus the
// per-file publish run, with the Launch OS API passed in so it can be tested without a server.

export type DroppedPage = { key: string; filename: string; html: string };
export type Step = "queued" | "checking" | "saving" | "publishing" | "waiting" | "live" | "kept" | "failed";
export type RowState = { key: string; filename: string; name: string; slug: string; step: Step; pct: number; url?: string; note?: string; pageId?: string; created?: boolean; previousHtml?: string };

export const STEP_TEXT: Record<Step, string> = {
  queued: "Waiting…",
  checking: "Checking the address…",
  saving: "Uploading…",
  publishing: "Publishing…",
  waiting: "Waiting for you…",
  live: "Live",
  kept: "Kept the live page",
  failed: "Didn't go live",
};
export const STEP_PCT: Record<Step, number> = { queued: 4, checking: 25, saving: 55, publishing: 85, waiting: 60, live: 100, kept: 100, failed: 100 };

export const isPageFile = (name: string) => /\.html?$/i.test(name) && !/^\._/.test(name);

// Pick a free address: try the suggested one, then name-2, name-3… (shared account folder means
// another site's page can already own an address).
export const freeSlug = async (slug: string, isFree: (s: string) => Promise<boolean>, tries = 8) => {
  if (await isFree(slug)) return { slug, changed: false };
  for (let n = 2; n < 2 + tries; n++) {
    const s = `${slug}-${n}`;
    if (await isFree(s)) return { slug: s, changed: true };
  }
  return null;
};

// Read everything that was dropped: files, and folders (walked recursively). Non-pages are counted.
export const readDrop = async (dt: DataTransfer): Promise<{ pages: DroppedPage[]; skipped: string[] }> => {
  const files: File[] = [];
  const entries = Array.from(dt.items || []).map((i) => (i as any).webkitGetAsEntry?.()).filter(Boolean);
  const walk = (entry: any): Promise<void> =>
    new Promise((res) => {
      if (entry.isFile) entry.file((f: File) => { files.push(f); res(); }, () => res());
      else {
        const reader = entry.createReader();
        const all: any[] = [];
        const next = () => reader.readEntries(async (batch: any[]) => {
          if (!batch.length) { for (const e of all) await walk(e); res(); } else { all.push(...batch); next(); }
        }, () => res());
        next();
      }
    });
  if (entries.length) for (const e of entries) await walk(e);
  else files.push(...Array.from(dt.files || []));
  return readFiles(files);
};

export const readFiles = async (files: File[]) => {
  const pages: DroppedPage[] = [];
  const skipped: string[] = [];
  for (const [i, f] of files.entries()) {
    if (isPageFile(f.name)) pages.push({ key: `${i}-${f.name}`, filename: f.name, html: await f.text() });
    else if (!/^\.|\.DS_Store$/.test(f.name)) skipped.push(f.name);
  }
  return { pages, skipped };
};

export const titleOnly = (html: string) => {
  const m = /<title[^>]*>[^<]*<\/title>/i.exec(html || "");
  return m ? m[0] : "";
};

// Creating a page returns the site's new canvas object, not the page, so find the page by its
// address in the site (addresses are unique per account).
export const pageIdBySlug = (campaign: any, slug: string): string | undefined =>
  ((campaign?.objects || []) as any[]).map((o) => o?.page).find((p) => p && p.slug === slug && !p.deleted)?.id;

// HPF-2: the largest page Launch Sites accepts (matches the server; the upload limit in front of the
// server is 15 MB, and a page travels as text inside a request)
export const MAX_PAGE_BYTES = 14 * 1024 * 1024;
const mb = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1).replace(/\.0$/, "")} MB`;
// UTF-8 size without TextEncoder (not in every environment the app's tests run in)
const utf8Bytes = (s: string) => {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff) { n += 4; i++; } // a pair of UTF-16 halves = one 4-byte character
    else n += 3;
  }
  return n;
};
export const pageTooBig = (html: string): string | null => {
  const bytes = utf8Bytes(html || "");
  return bytes > MAX_PAGE_BYTES ? `This page is ${mb(bytes)}. Pages can be up to ${mb(MAX_PAGE_BYTES)}.` : null;
};
